/**
 * Unwrap a CJS default import that the bundler handed back one level too deep.
 *
 * For a CJS package that sets `__esModule` **and** `exports.default`, Rollup (Vite 6)
 * unwrapped `import X from 'pkg'` to the real value, while Rolldown (Vite 8) returns the
 * module namespace `{ …, default: <the real value> }`. Rendering that namespace throws
 * React error #130 ("got: object"), which is what took out admin-web's "Show QR" and every
 * JSON modal in production on 2026-08-22.
 *
 * Calling this at the import site is correct under both bundlers: Rollup's default has no
 * `.default` to find, so it falls through to the value itself. Keep the call until the
 * dependency ships ESM.
 *
 * `scripts/check-cjs-interop.mjs` looks for `unwrapCjsDefault(` in a file before it will
 * accept that file as the handling site for an allowlisted package, so this is the one
 * spelling of the unwrap the guard recognises — inline variants are not detected.
 *
 * See kb/ops/vite-8-rolldown-cjs-default-interop.md.
 */
export const unwrapCjsDefault = <T>(imported: T): T => {
  const maybeWrapped = imported as unknown as { default?: T } | null | undefined
  if (maybeWrapped === null || maybeWrapped === undefined) return imported
  if (typeof maybeWrapped !== 'object' && typeof maybeWrapped !== 'function') return imported
  return maybeWrapped.default ?? imported
}
